import { Controller } from "@hotwired/stimulus"
import { Tooltip } from "bootstrap"
import { disposeTooltip } from "../utils/bootstrap-tooltip.util"
import { formatTime } from "../utils/date-format.util"
import { hasHoverPointer } from "../utils/pointer.util"

// A host whose tap does something — so a tooltip on it is a tap stolen. Covers
// the wrapper form too (`data-bs-toggle` sits on the toggle INSIDE the
// `.dropdown` the controller is mounted on), which is the case this exists for.
const TOUCH_ACTIONABLE_HOST = "a[href], button, input, select, textarea, [role='button'], [data-bs-toggle]"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  loadingTitle: ""
}

// Connects to data-controller="tooltip"
export default class extends Controller {
  static values = {
    translations: { type: Object, default: {} },
    placement: { type: String, default: "top" },
    class: String,
    title: String,
    html: { type: Boolean, default: true },
    truncatedOnly: { type: Boolean, default: false },
    schedule: String
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.#initializeTooltip()
    this.#bindStateHandlers()
    this.#bindHostedDropdownHandlers()
    this.#bindLabelSuppression()
  }

  // Bootstrap bakes the title into the Tooltip instance at construction, so a
  // controller that rewrites data-tooltip-title-value afterwards changed the
  // attribute and nothing else — the tooltip kept showing whatever the server
  // rendered. (logo#displayLogo restating a logo's origin is the case that
  // exposed this: the ✕ still said "origin unknown" over a logo fetched from a
  // website the user had just typed.) Stimulus calls this on every change,
  // including the initial one, so guard on the instance existing.
  titleValueChanged() {
    if (!this.tooltip) return

    this.tooltip.setContent({ ".tooltip-inner": this.#baseTitle() })
  }

  disconnect() {
    this.#cleanupTooltip()
  }

  #initializeTooltip() {
    if (this.#shouldStandDownForTouch()) return

    // For input fields, show the current value as tooltip
    if (this.element.tagName === "INPUT") {
      this.#setupInputTooltip()
    } else if (this.truncatedOnlyValue) {
      this.#setupTruncatedTooltip()
    } else {
      const options = {
        placement: this.placementValue,
        customClass: this.classValue || "",
        title: this.#baseTitle(),
        html: this.htmlValue
      }
      this.tooltip = new Tooltip(this.element, options)
    }
  }

  // On iOS Safari the first tap on an element whose hover state changes the page
  // is spent applying :hover, and that tap's click is discarded. A tooltip is a
  // hover affordance, so showing one costs the tap — which is how the table row
  // "…" menu came to need two of them: its tooltip sits on the .dropdown
  // wrapper (see #bindHostedDropdownHandlers), so tap one only painted the
  // tooltip.
  //
  // Only ACTIONABLE hosts stand down. A tooltip whose host does nothing when
  // tapped — the lavender "?" help icons, a truncated cell, the complaint glyph
  // — costs no interaction and is the only place its text exists on a phone;
  // removing those would delete content rather than fix a defect. A loading
  // title is kept regardless: `tooltip:set-loading` shows it programmatically as
  // a download-progress indicator, not on hover.
  //
  // Everything else in this controller null-guards on `this.tooltip`, so
  // leaving the instance unbuilt is safe.
  #shouldStandDownForTouch() {
    if (hasHoverPointer() || this.translationsValue.loadingTitle !== undefined) return false

    return this.element.matches(TOUCH_ACTIONABLE_HOST) || this.element.querySelector(TOUCH_ACTIONABLE_HOST) !== null
  }

  #bindStateHandlers() {
    this.setLoadingState = () => this.#setLoadingState()
    this.resetState = () => this.#resetState()

    this.element.addEventListener("tooltip:set-loading", this.setLoadingState)
    this.element.addEventListener("tooltip:reset", this.resetState)
  }

  // Bootstrap stores exactly ONE component instance per element: Data.set
  // refuses a second and logs "Bootstrap doesn't allow more than one instance
  // per element. Bound instance: bs.tooltip." So a Tooltip bound to an element
  // that is ALSO `data-bs-toggle="dropdown"` silently breaks that dropdown —
  // Dropdown.getOrCreateInstance() cannot store its instance, so the menu still
  // opens (the object it returns works once) but clearMenus() looks it up with
  // Dropdown.getInstance(), gets null, and skips it. The menu then never closes
  // on an outside click.
  //
  // Such markup therefore puts this controller on the `.dropdown` WRAPPER, not
  // on the toggle. The wrapper cannot become the toggle's parent-in-between,
  // because Bootstrap finds the menu with SelectorEngine.next() — it must stay
  // the toggle's next SIBLING, which rules out wrapping the button.
  //
  // The cost of hosting the tooltip on the wrapper is this handler: the pointer
  // is still inside the wrapper once the menu opens, so without it the tooltip
  // hangs over the open menu (and re-shows on every hover across it). disable()
  // rather than hide() for that second reason.
  #bindHostedDropdownHandlers() {
    if (!this.element.querySelector('[data-bs-toggle="dropdown"]')) return

    this.disableForDropdown = () => {
      this.tooltip?.hide()
      this.tooltip?.disable()
    }
    this.enableAfterDropdown = () => this.tooltip?.enable()

    this.element.addEventListener("show.bs.dropdown", this.disableForDropdown)
    this.element.addEventListener("hidden.bs.dropdown", this.enableAfterDropdown)
  }

  // A tooltip that restates a label already on screen is noise; a control whose
  // label is hidden needs one. The builder header's action labels come and go
  // with the header's width (a container query in _page.scss), which no
  // viewport rule in _tooltip.scss can follow — so a button marks the label it
  // would restate with `data-tooltip-label`, and the tooltip stands down for
  // exactly as long as that label is visible. Not while loading: the download
  // button's progress title is not the label and must show either way.
  #bindLabelSuppression() {
    const label = this.element.querySelector("[data-tooltip-label]")
    if (!label) return

    this.suppressForVisibleLabel = (event) => {
      if (this._preventHide) return
      if (getComputedStyle(label).display === "none") return

      event.preventDefault()
    }
    this.element.addEventListener("show.bs.tooltip", this.suppressForVisibleLabel)
  }

  #setupInputTooltip() {
    this.updateTooltip = () => {
      const value = this.element.value
      if (value?.trim()) {
        if (!this.tooltip) {
          this.tooltip = new Tooltip(this.element, {
            placement: this.placementValue,
            customClass: this.classValue || "",
            title: value,
            html: this.htmlValue
          })
        } else {
          this.tooltip.setContent({ ".tooltip-inner": this.#escapeHtml(value) })
        }
      } else {
        if (this.tooltip) {
          disposeTooltip(this.tooltip)
          this.tooltip = null
        }
      }
    }

    this.hideTooltip = () => {
      if (this.tooltip) {
        disposeTooltip(this.tooltip)
        this.tooltip = null
      }
    }

    // Hide tooltip while typing, show on blur
    this.element.addEventListener("input", this.hideTooltip)
    this.element.addEventListener("focus", this.hideTooltip)
    this.element.addEventListener("blur", this.updateTooltip)

    // Initial setup - show tooltip if there's existing content
    this.updateTooltip()
  }

  #setupTruncatedTooltip() {
    this.showIfTruncated = () => {
      if (!this.#isTextTruncated()) {
        this.tooltip?.hide()
        return
      }

      if (!this.tooltip) {
        this.tooltip = new Tooltip(this.element, {
          placement: this.placementValue,
          customClass: this.classValue || "",
          title: this.#baseTitle(),
          html: this.htmlValue,
          trigger: "manual"
        })
      } else {
        this.tooltip.setContent({ ".tooltip-inner": this.#baseTitle() })
      }

      this.tooltip.show()
    }

    this.hideTruncatedTooltip = () => {
      this.tooltip?.hide()
    }

    this.element.addEventListener("mouseenter", this.showIfTruncated)
    this.element.addEventListener("focus", this.showIfTruncated)
    this.element.addEventListener("mouseleave", this.hideTruncatedTooltip)
    this.element.addEventListener("blur", this.hideTruncatedTooltip)
  }

  #cleanupTooltip() {
    clearTimeout(this._transitionTimeout)

    // Remove event listeners
    if (this.updateTooltip) {
      this.element.removeEventListener("blur", this.updateTooltip)
    }
    if (this.hideTooltip) {
      this.element.removeEventListener("input", this.hideTooltip)
      this.element.removeEventListener("focus", this.hideTooltip)
    }
    if (this.setLoadingState) {
      this.element.removeEventListener("tooltip:set-loading", this.setLoadingState)
    }
    if (this.resetState) {
      this.element.removeEventListener("tooltip:reset", this.resetState)
    }
    if (this.showIfTruncated) {
      this.element.removeEventListener("mouseenter", this.showIfTruncated)
      this.element.removeEventListener("focus", this.showIfTruncated)
    }
    if (this.hideTruncatedTooltip) {
      this.element.removeEventListener("mouseleave", this.hideTruncatedTooltip)
      this.element.removeEventListener("blur", this.hideTruncatedTooltip)
    }
    if (this._preventHide) {
      this.element.removeEventListener("hide.bs.tooltip", this._preventHide)
      this._preventHide = null
    }
    if (this.disableForDropdown) {
      this.element.removeEventListener("show.bs.dropdown", this.disableForDropdown)
      this.disableForDropdown = null
    }
    if (this.enableAfterDropdown) {
      this.element.removeEventListener("hidden.bs.dropdown", this.enableAfterDropdown)
      this.enableAfterDropdown = null
    }
    if (this.suppressForVisibleLabel) {
      this.element.removeEventListener("show.bs.tooltip", this.suppressForVisibleLabel)
      this.suppressForVisibleLabel = null
    }

    // Dispose of tooltip instance
    if (this.tooltip) {
      disposeTooltip(this.tooltip)
      this.tooltip = null
    }
  }

  #baseTitle() {
    let title = this.titleValue || this.element.getAttribute("title")

    if (this.hasScheduleValue && title) {
      const localTimes = this.scheduleValue.split(",").map((utcHour) => {
        const date = new Date()
        date.setUTCHours(parseInt(utcHour, 10), 0, 0, 0)
        return formatTime(date)
      })
      title = title.replace(/\d{1,2}:\d{2}(,\s*\d{1,2}:\d{2})*/g, localTimes.join(", "))
    }

    return title
  }

  #isTextTruncated() {
    return (
      this.element.scrollWidth > this.element.clientWidth + 1 ||
      this.element.scrollHeight > this.element.clientHeight + 1
    )
  }

  #escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  #transitionTooltipState(isLoading) {
    // Bootstrap 5.3 internal property — verify on major upgrade
    const tipElement = this.tooltip?.tip
    if (!tipElement) return

    tipElement.classList.toggle("is-loading", isLoading)
    tipElement.classList.add("is-content-changing")
    clearTimeout(this._transitionTimeout)
    this._transitionTimeout = window.setTimeout(() => {
      tipElement.classList.remove("is-content-changing")
    }, 180)
  }

  #setLoadingState() {
    if (!this.tooltip || this.element.tagName === "INPUT" || this.translationsValue.loadingTitle === undefined) return

    // Prevent Bootstrap from auto-hiding on mouseout/blur while loading
    this._preventHide = (event) => event.preventDefault()
    this.element.addEventListener("hide.bs.tooltip", this._preventHide)

    this.tooltip.setContent({
      ".tooltip-inner": `${this.#escapeHtml(this.#texts.loadingTitle)}<span class="tooltip-loading-spinner" aria-hidden="true"></span>`
    })
    this.tooltip.show()
    this.#transitionTooltipState(true)
    this.tooltip.update()
  }

  #resetState() {
    if (!this.tooltip || this.element.tagName === "INPUT") return

    // Allow tooltip to hide normally again
    if (this._preventHide) {
      this.element.removeEventListener("hide.bs.tooltip", this._preventHide)
      this._preventHide = null
    }

    this.tooltip.setContent({ ".tooltip-inner": this.#baseTitle() })
    this.tooltip.hide()
    this.#transitionTooltipState(false)
  }
}
